import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, ShieldCheck } from 'lucide-react';
import brandLogo from '../assets/Dinelyt_Transparent_Logo.png';
import brandTagline from '../assets/Dinelyt_Transparent_Tagline.png';

type LegalSection = {
  title: string;
  body: string[];
};

interface LegalPageLayoutProps {
  eyebrow: string;
  title: string;
  intro: string;
  effectiveDate: string;
  sections: LegalSection[];
}

const LegalPageLayout: React.FC<LegalPageLayoutProps> = ({
  eyebrow,
  title,
  intro,
  effectiveDate,
  sections,
}) => {
  React.useEffect(() => {
    const previousBodyBackground = document.body.style.backgroundColor;
    window.scrollTo(0, 0);
    document.body.style.backgroundColor = '#050505';

    return () => {
      document.body.style.backgroundColor = previousBodyBackground;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-gray-100">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute left-[-10%] top-[-8rem] h-80 w-80 rounded-full bg-orange-500/18 blur-3xl" />
        <div className="absolute right-[-8%] top-40 h-72 w-72 rounded-full bg-amber-300/10 blur-3xl" />
        <div className="absolute inset-x-0 top-0 h-72 bg-[radial-gradient(circle_at_top,rgba(251,146,60,0.16),transparent_60%)]" />
      </div>

      <div className="relative mx-auto flex min-h-screen w-full max-w-6xl flex-col px-4 pb-12 pt-5 sm:px-6 lg:px-8">
        <header className="mb-8 flex flex-col gap-4 rounded-[24px] border border-white/10 bg-white/5 px-4 py-4 backdrop-blur-xl sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-orange-300 transition hover:border-orange-400/60 hover:text-orange-200"
              aria-label="Back to home"
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>

            <Link to="/" className="flex min-w-0 flex-col items-start">
              <img src={brandLogo} alt="Dinelyt Logo" className="h-7 w-auto object-contain" />
              <img src={brandTagline} alt="Dinelyt Tagline" className="mt-0.5 h-3 w-auto object-contain" />
            </Link>

            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-300">
              <Link to="/Terms_and_Conditions" className="rounded-full border border-white/10 px-3 py-1.5 transition hover:border-orange-400/60 hover:text-white">
                Terms
              </Link>
              <Link to="/PrivacyPolicy" className="rounded-full border border-white/10 px-3 py-1.5 transition hover:border-orange-400/60 hover:text-white">
                Privacy
              </Link>
            </div>
          </div>

          <nav className="flex flex-wrap items-center gap-2 text-xs text-gray-300">
            <Link to="/restaurant/onboarding" className="rounded-full bg-gradient-to-r from-orange-500 to-orange-600 px-3 py-1.5 font-semibold text-white transition hover:brightness-110">
              Start With Dinelyt
            </Link>
          </nav>
        </header>

        <main className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
          <section className="rounded-[32px] border border-white/10 bg-[#0d0d0d]/90 p-6 shadow-2xl shadow-black/30 sm:p-8 lg:p-10">
            <div className="max-w-3xl">
              <p className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-orange-400/30 bg-orange-500/10 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-orange-200">
                <ShieldCheck className="h-4 w-4" />
                {eyebrow}
              </p>
              <h1 className="text-2xl font-black tracking-tight text-white sm:text-3xl">{title}</h1>
              <p className="mt-2.5 text-xs leading-5 text-gray-300 sm:text-sm">{intro}</p>
            </div>

            <div className="mt-5 flex flex-wrap gap-2 text-[11px] text-gray-300">
              <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1">
                Effective date: {effectiveDate}
              </span>
              <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1">
                Brand: Dinelyt
              </span>
              <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1">
                Product: dine-in ordering and restaurant operations platform
              </span>
            </div>

            <div className="mt-8 space-y-6">
              {sections.map((section) => (
                <section key={section.title} className="rounded-3xl border border-white/8 bg-white/[0.03] p-4">
                  <h2 className="text-base font-bold text-white sm:text-lg">{section.title}</h2>
                  <div className="mt-2.5 space-y-2.5">
                    {section.body.map((paragraph) => (
                      <p key={paragraph} className="text-[11px] leading-5 text-gray-300 sm:text-xs">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </section>

          <aside className="space-y-6">
            <div className="rounded-[28px] border border-white/10 bg-white/5 p-6 backdrop-blur-xl">
              <h2 className="text-lg font-bold text-white">Need help?</h2>
              <p className="mt-3 text-sm leading-6 text-gray-300">
                If you have questions about these policies, want to report a concern, or need help as a restaurant partner or diner, contact us directly.
              </p>

              <div className="mt-5 space-y-3 text-sm text-gray-200">
                <a href="mailto:info@dinelyt.com" className="flex items-center gap-3 rounded-2xl border border-white/10 px-4 py-3 transition hover:border-orange-400/50">
                  <Mail className="h-4 w-4 text-orange-300" />
                  info@dinelyt.com
                </a>
                <a href="tel:+919587932716" className="flex items-center gap-3 rounded-2xl border border-white/10 px-4 py-3 transition hover:border-orange-400/50">
                  <Phone className="h-4 w-4 text-orange-300" />
                  +91 95879 32716
                </a>
              </div>
            </div>

            <div className="rounded-[28px] border border-white/10 bg-gradient-to-br from-orange-500/10 to-transparent p-6">
              <h2 className="text-lg font-bold text-white">What Dinelyt covers</h2>
              <ul className="mt-4 space-y-3 text-sm leading-6 text-gray-300">
                <li>Restaurant onboarding and digital setup</li>
                <li>QR-based table ordering and dine-in sessions</li>
                <li>Menu, billing, payment and invoice workflows</li>
                <li>Operational dashboards, analytics and team access</li>
              </ul>
            </div>
          </aside>
        </main>
      </div>
    </div>
  );
};

export type { LegalSection };
export default LegalPageLayout;
